import { strContains, getCookie, timeInSeconds } from '@/utils';

import {
	handleOrConditionArray,
	getJsFromString,
	testCondition,
	getDeviceType,
	getCssSelectorTextContent,
	prepareDateComparison,
	prepareTimeComparison,
	parseUrlString,
} from './utils';

export function pause(isPaused) {
	return !isPaused;
}

export function location(locations = []) {
	const url = parseUrlString(document.location.href);

	//location uses 'exists', 'does not exist' for contains/does not contain
	const operatorMap = {
		exists: 'contains',
		'does not exist': 'does not contain',
	};

	return handleOrConditionArray(locations, locationCondition => {
		return testCondition({
			value1: url,
			value2: parseUrlString(locationCondition.value),
			operator: operatorMap[locationCondition.operator] ?? locationCondition.operator,
			regex: locationCondition.regex,
		});
	});
}

export function referrer(referrers = [], { state }) {
	if (referrers.length === 0) return true;

	const url = parseUrlString(state.referrer || document.referrer);

	//location uses 'exists', 'does not exist' for contains/does not contain
	const operatorMap = {
		exists: 'contains',
		'does not exist': 'does not contain',
	};

	return handleOrConditionArray(referrers, referrerCondition => {
		if (typeof referrerCondition === 'string') {
			return testCondition({
				value1: url,
				value2: parseUrlString(referrerCondition),
				operator: 'contains',
				regex: false,
			});
		}
		return testCondition({
			value1: url,
			value2: parseUrlString(referrerCondition.value),
			operator: operatorMap[referrerCondition.operator] ?? referrerCondition.operator,
			regex: referrerCondition.regex,
		});
	});
}

export function cookie(cookies = []) {
	return handleOrConditionArray(cookies, cookieCondition =>
		testCondition({
			value1: getCookie(cookieCondition.name),
			operator: cookieCondition.operator,
			value2: cookieCondition.value,
		})
	);
}

export function js(jsVars = []) {
	return handleOrConditionArray(jsVars, jsVarCondition =>
		testCondition({
			value1: getJsFromString(jsVarCondition.name),
			operator: jsVarCondition.operator,
			value2: jsVarCondition.value,
		})
	);
}

export function css_selector(cssSelectors = []) {
	return handleOrConditionArray(cssSelectors, cssSelectorCondition =>
		testCondition({
			value1: getCssSelectorTextContent(cssSelectorCondition.name),
			operator: cssSelectorCondition.operator,
			value2: cssSelectorCondition.value,
		})
	);
}

export function number_of_pages({ check, amount } = {}, { state }) {
	if (!check) {
		return true;
	}

	if (check === 'min') {
		return state.pageCount >= Number(amount);
	}

	if (check === 'max') {
		return state.pageCount <= Number(amount);
	}
}

export function devices(devices = []) {
	const deviceData = getDeviceType();
	return devices.some(deviceType => {
		if (deviceType === 'desktop') {
			return !deviceData.phone && !deviceData.seven_inch && !deviceData.tablet;
		}

		if (deviceType === 'tablet') {
			return deviceData.tablet || deviceData.seven_inch;
		}

		if (deviceType === 'mobile') {
			return deviceData.phone || deviceData.seven_inch;
		}
	});
}

export function date(dateCondition = {}) {
	return testCondition(prepareDateComparison(dateCondition));
}

export function clock(timeCondition = {}) {
	return testCondition(prepareTimeComparison(timeCondition));
}

export function type(newOrReturning, { state }) {
	return (newOrReturning === 'new' && !state.returning) || (newOrReturning === 'returning' && state.returning);
}

export function percentage(perc, { state }) {
	return parseFloat(perc) > parseFloat(state.percentage);
}

export function scrollpos(scrollpos = {}) {
	const { from = 'top', px } = scrollpos;

	const docHeight = document.body.scrollHeight;
	const winHeight = window.innerHeight;
	const scrollTop = (window.pageYOffset || document.scrollTop) - (document.clientTop || 0);
	const scrollTopPerc = ((scrollTop + winHeight) / docHeight) * 100;
	const scrollBottom = docHeight - (scrollTop + winHeight);
	const scrollBottomPerc = 100 - scrollTopPerc;

	const usePerc = isNaN(px) && String(px).indexOf('%') > -1;
	const pixels = Number(px);
	const perc = Number(String(px).replace('%', ''));

	return (
		(from === 'top' && !usePerc && scrollTop >= pixels) ||
		(from === 'top' && usePerc && scrollTopPerc >= perc) ||
		(from === 'bottom' && !usePerc && scrollBottom <= pixels) ||
		(from === 'bottom' && usePerc && scrollBottomPerc <= perc)
	);
}

export function time(time, { state }) {
	const currentTime = timeInSeconds();
	const timeOnPage = state.timeOnPage;

	if (typeof timeOnPage === 'undefined') {
		return false;
	}

	return currentTime - timeOnPage >= Number(time);
}

export function time_after_valid(timeAfterValid, { state }) {
	const currentTime = timeInSeconds();
	const timeAfterValidOnPage = state.timeAfterValid;

	if (typeof timeAfterValidOnPage === 'undefined') {
		return false;
	}

	return currentTime - timeAfterValidOnPage >= Number(timeAfterValid);
}

export function time_in_session(timeInSession, { state }) {
	return state.sessionTime >= Number(timeInSession);
}

export function event_listeners(eventListeners = [], { state }) {
	return handleOrConditionArray(eventListeners, listenerCondition => {
		const { selector, event, operator } = listenerCondition;
		return testCondition({
			value1: state.eventListeners?.[selector],
			value2: event,
			operator,
		});
	});
}

export function form_events(formEvents = [], { state }) {
	return handleOrConditionArray(formEvents, formEventCondition => {
		const { formKey, event, operator } = formEventCondition;
		return testCondition({
			value1: state.formEvents?.[formKey],
			value2: event,
			operator,
		});
	});
}
